import ElakayImage from '../../components/images/ElakayImage';
// <DefaultPromoSlide> -- one resolved slide of a default promotional
// composition: background layers, the image (responsive srcset, optional
// motion), then the text boxes drawn by the existing ThreeDText engine.
//
// Everything arrives resolved by the server engine (no randomness here):
// boxes in % of the frame, text size in % of the frame WIDTH turned into px
// from the measured frame (so ThreeDText's px-based stroke / shadow / glow
// metrics stay proportional), then shrunk once if the text would overflow
// its box. Blend presets ("fusion") are CSS-only: mix-blend-mode, mask-image,
// background-clip:text, filter, backdrop-filter -- each with a readable
// fallback where the browser lacks it.
import React, { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import ThreeDText from "../pageInside/threedtext/threeDtext";
import { toImageUrl } from "../../config";

const SMALL_FRAME = 480;
const SECONDARY_ROLES = new Set(["description", "custom"]);

// Relative luminance of a #hex / rgb() color (0 = black, 1 = white).
const luminance = (value) => {
  const text = String(value || "").trim();
  let r; let g; let b;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})/i.exec(text);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const n = parseInt(h, 16);
    [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  } else {
    const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/.exec(text);
    if (!rgb) return 1;
    [r, g, b] = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }
  const c = (v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
};

export const replaceShopToken = (text, shopName) => String(text || "").replace(/\{shop\}/g, shopName || "Elakay");

const hexToRgba = (value, alpha) => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || "").trim());
  if (!match) return value;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const n = parseInt(hex, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

const animationStyle = (animation) => (animation && animation.type && animation.type !== "none" ? {
  "--dpa-duration": `${animation.duration}ms`,
  "--dpa-delay": `${animation.delay}ms`,
  "--dpa-iter": animation.repeat === 0 ? "infinite" : String(animation.repeat || 1),
  "--dpa-dir": animation.direction || "normal",
  "--dpa-ease": animation.easing === "spring" ? "cubic-bezier(.34,1.56,.64,1)" : animation.easing === "smooth" ? "cubic-bezier(.22,.61,.36,1)" : (animation.easing || "ease-out"),
} : null);

// ── Background ───────────────────────────────────────────────────────
const backgroundLayer = (background) => {
  if (!background) return null;
  const { type, color, gradient, opacity = 1 } = background;
  const linear = gradient ? `linear-gradient(${gradient.angle}deg, ${gradient.from}, ${gradient.to})` : null;
  switch (type) {
    case "color": return { background: color, opacity };
    case "gradient": return { background: linear, opacity };
    case "transparent": return { background: hexToRgba(color, Math.min(opacity, 0.6)) };
    case "glass": return { background: `linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.06)), ${linear || color}`, opacity };
    default: return null;
  }
};
// Tints drawn ABOVE the image (image + color / image + gradient).
const tintLayer = (background) => {
  if (!background) return null;
  if (background.type === "imageColor") return { background: background.color, opacity: Math.min(background.opacity, 0.85), mixBlendMode: "multiply" };
  if (background.type === "imageGradient") {
    const g = background.gradient;
    return { background: `linear-gradient(${g.angle}deg, ${hexToRgba(g.from, 0.85)}, ${hexToRgba(g.to, 0.15)})`, opacity: Math.min(background.opacity, 0.9) };
  }
  return null;
};

// ── Image ────────────────────────────────────────────────────────────
const SlideImage = ({ image, priority, onLoad }) => {
  const [failed, setFailed] = useState(false);
  const full = toImageUrl(image.url);
  if (!full || failed) return <div className="dpromo-image-fallback" aria-hidden="true" />;
  return (
    <ElakayImage
      preset="banner"
      className="dpromo-image"
      src={full}
      sizes="100vw"
      alt={image.alt || ""}
      width={image.width || undefined}
      height={image.height || undefined}
      loading={priority === "high" ? "eager" : "lazy"}
      // React 18 does not know fetchPriority yet; browsers read the attribute.
      fetchpriority={priority === "high" ? "high" : priority === "later" ? "low" : "auto"}
      decoding="async"
      draggable={false}
      style={{ objectFit: image.fit || "cover", objectPosition: `${image.focusX ?? 50}% ${image.focusY ?? 50}%` }}
      onLoad={(event) => {
        event.currentTarget.closest('.dpromo-slide')?.style.setProperty('--dpromo-image-url', `url("${event.currentTarget.currentSrc || event.currentTarget.src}")`);
        onLoad?.(event);
      }}
      onError={() => { setFailed(true); if (onLoad) onLoad(); }}
    />
  );
};

// ── Text ─────────────────────────────────────────────────────────────
const cardBackground = (card, colors) => {
  switch (card?.type) {
    case "glass": return { type: "glass", color: "#ffffff", opacity: card.opacity ?? 0.12, accent: card.accent ? (colors.color || colors.glow) : undefined, maxWidth: "100%", padding: "0.35em 0.6em" };
    case "color": return { type: "color", color: card.color || "#0b1220", radius: "10px", padding: "0.3em 0.6em" };
    case "transparent": return { type: "transparent", color: card.color || "#000000", opacity: card.opacity ?? 0.45, radius: "10px", padding: "0.3em 0.6em" };
    default: return { type: "none" };
  }
};

// Text size: px from the frame width, then ONE shrink pass if the rendered
// text overflows its box (never below 85 % of the readable minimum). The
// factor is keyed by what it was measured for, so a new size / text starts
// again from 1 without an extra reset render.
const useFittedSize = (boxRef, contentRef, basePx, minPx, measureKey) => {
  const [fit, setFit] = useState({ key: measureKey, factor: 1 });
  const factor = fit.key === measureKey ? fit.factor : 1;
  useLayoutEffect(() => {
    if (factor !== 1) return;
    const box = boxRef.current;
    const content = contentRef.current;
    if (!box || !content) return;
    const bw = box.clientWidth;
    const bh = box.clientHeight;
    const cw = content.scrollWidth;
    const ch = content.scrollHeight;
    if (!bw || !bh || !cw || !ch) return;
    const ratio = Math.min(bw / cw, (bh * 1.08) / ch);
    if (ratio < 0.99) {
      const floor = Math.min(1, (minPx * 0.85) / basePx);
      setFit({ key: measureKey, factor: Math.max(floor, ratio * 0.97) });
    }
  }, [boxRef, contentRef, basePx, minPx, measureKey, factor]);
  return factor;
};

const PlainClipText = ({ text, frame, boxRef, colors, sizePx }) => {
  const spanRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    const span = spanRef.current;
    const box = boxRef.current;
    if (!span || !box) return;
    setOffset({ x: box.offsetLeft + span.offsetLeft, y: box.offsetTop + span.offsetTop });
  }, [boxRef, frame.width, frame.height, text, sizePx]);
  return (
    <span
      ref={spanRef}
      className="dpromo-clip-text"
      style={{
        fontSize: `${sizePx}px`,
        backgroundImage: "var(--dpromo-image-url, none)",
        backgroundSize: `${frame.width}px ${frame.height}px`,
        backgroundPosition: `${-offset.x}px ${-offset.y}px`,
        "--dpromo-clip-stroke": hexToRgba(colors.outline || "#000000", 0.55),
      }}
    >
      {String(text).split(".;").map((line, index) => <span className="dpromo-clip-line" key={index}>{line}</span>)}
    </span>
  );
};

const TextLayer = ({ text, frame, shopName, image, compact }) => {
  const boxRef = useRef(null);
  const contentRef = useRef(null);
  const content = replaceShopToken(text.text, shopName);
  const { style } = text;
  const raw = (frame.width * style.sizePct) / 100;
  const basePx = Math.max(style.minPx || 10, Math.min(style.maxPx || 140, raw));
  const factor = useFittedSize(boxRef, contentRef, basePx, style.minPx || 10, `${Math.round(frame.width)}x${Math.round(frame.height)}|${content}|${basePx}|${style.variant}`);
  const sizePx = Math.round(basePx * factor * 10) / 10;
  const useClip = style.blend === "imageClip" && image && image.url;
  const tune = style.density !== 1 || style.depth !== 1
    ? { density: style.density, shadowK: 0.08 * style.depth, blurK: 0.12 * style.depth, outlineK: 0.048 * Math.max(0.5, style.depth) }
    : undefined;
  const justify = text.align === "left" ? "flex-start" : text.align === "right" ? "flex-end" : "center";
  const alignItems = text.valign === "start" ? "flex-start" : text.valign === "end" ? "flex-end" : "center";
  return (
    <div
      ref={boxRef}
      className={`dpromo-text dpromo-role-${text.role} dpromo-blend-${style.blend} dpromo-card-${style.card?.type || "none"}${compact ? " is-compact" : ""}${luminance(style.colors?.text) < 0.25 ? " dpromo-ink-dark" : ""}`}
      data-text-id={text.id}
      style={{
        left: `${text.box.x}%`,
        top: `${text.box.y}%`,
        width: `${text.box.w}%`,
        height: `${text.box.h}%`,
        zIndex: text.z,
        justifyContent: justify,
        alignItems,
        textAlign: text.align,
        opacity: style.opacity,
        "--dpromo-scrim": hexToRgba(style.card?.color || "#0b1220", Math.max(0.25, Math.min(0.75, style.card?.opacity ?? 0.45))),
        "--dpromo-glow": style.colors?.glow || "rgba(255,255,255,0.6)",
      }}
    >
      <div ref={contentRef} className={`dpromo-anim dpromo-anim-${text.animation?.type || "none"}`} style={animationStyle(text.animation) || undefined}>
        {useClip ? (
          <PlainClipText text={content} image={image} frame={frame} boxRef={boxRef} colors={style.colors || {}} sizePx={sizePx} />
        ) : (
          <ThreeDText
            text={content}
            variant={style.variant}
            colors={style.colors || {}}
            size={sizePx}
            placement="inline"
            textAlign={text.align}
            reflect={Boolean(style.reflect) && !compact}
            precision={style.precision}
            scaleMode={tune ? "custom" : "auto"}
            tune={tune}
            zIndex={1}
            background={cardBackground(style.card, style.colors || {})}
          />
        )}
      </div>
    </div>
  );
};

const DefaultPromoSlide = ({
  slide, frame, shopName, active = true, animate = true, priority = "normal", onImageLoad, selectedTextId = null, onSelectText,
}) => {
  const compact = frame.width < SMALL_FRAME;
  // Small frames keep the essential texts only (title / subtitle / promotion
  // / call to action, at most 3): legibility first on phones.
  const texts = useMemo(() => {
    if (!compact) return slide.texts;
    return slide.texts.filter((text) => !SECONDARY_ROLES.has(text.role)).slice(0, 3);
  }, [slide.texts, compact]);
  const background = backgroundLayer(slide.background);
  const tint = tintLayer(slide.background);
  const overlay = slide.background?.overlay;
  const imageAnim = slide.image ? slide.imageAnimation : null;
  return (
    <div
      className={`dpromo-slide${active ? " is-active" : ""}${animate ? "" : " dpromo-static"}`}
      data-template={slide.template}
      data-slide-id={slide.id}
    >
      {background && <div className="dpromo-layer dpromo-bg" style={background} aria-hidden="true" />}
      {active && slide.image && (
        <div className={`dpromo-layer dpromo-image-wrap dpromo-anim dpromo-img-${imageAnim?.type || "none"}`} style={animationStyle(imageAnim) || undefined}>
          <SlideImage image={slide.image} priority={priority} onLoad={onImageLoad} />
        </div>
      )}
      {tint && <div className="dpromo-layer" style={tint} aria-hidden="true" />}
      {overlay && overlay.opacity > 0 && <div className="dpromo-layer" style={{ background: overlay.color, opacity: overlay.opacity }} aria-hidden="true" />}
      {texts.map((text) => (
        <TextLayer key={text.id} text={text} frame={frame} shopName={shopName} image={slide.image} compact={compact} />
      ))}
      {onSelectText && texts.map((text) => (
        // Editor only: transparent hit areas over each text box (drag to
        // move) + a corner handle on the selected one (drag to resize).
        <React.Fragment key={`hit-${text.id}`}>
          <button
            type="button"
            className={`dpromo-hit swiper-no-swiping${selectedTextId === text.id ? " is-selected" : ""}`}
            style={{ left: `${text.box.x}%`, top: `${text.box.y}%`, width: `${text.box.w}%`, height: `${text.box.h}%` }}
            onPointerDown={(event) => onSelectText(text, event, "move")}
            aria-label={replaceShopToken(text.text, shopName)}
            aria-pressed={selectedTextId === text.id ? "true" : "false"}
          />
          {selectedTextId === text.id && (
            <span
              className="dpe-resize swiper-no-swiping"
              style={{ left: `${text.box.x + text.box.w}%`, top: `${text.box.y + text.box.h}%` }}
              onPointerDown={(event) => onSelectText(text, event, "resize")}
              aria-hidden="true"
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default memo(DefaultPromoSlide);
